import { useEffect, useRef, useState } from 'react';
import { Coordinates } from '../types';

interface Props {
  isOpen: boolean;
  initialLocation: Coordinates | null;
  onCancel: () => void;
  onConfirm: (location: Coordinates) => void;
}

const DEFAULT_LOCATION: Coordinates = { lat: 20, lon: 0 };
const DEFAULT_ZOOM = 2;
const PIN_ZOOM = 8;
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

export function PinLocationDialog({ isOpen, initialLocation, onCancel, onConfirm }: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [draftLocation, setDraftLocation] = useState<Coordinates>(initialLocation ?? DEFAULT_LOCATION);
  const [mapReady, setMapReady] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setDraftLocation(initialLocation ?? DEFAULT_LOCATION);
  }, [isOpen, initialLocation?.lat, initialLocation?.lon]);

  useEffect(() => {
    if (!isOpen) return;
    const leaflet = (window as Window & { L?: any }).L;
    if (!leaflet || !mapContainerRef.current) {
      setMapReady(false);
      return;
    }

    setMapReady(true);
    const startingLocation = initialLocation ?? DEFAULT_LOCATION;
    const startingZoom = initialLocation ? PIN_ZOOM : DEFAULT_ZOOM;

    const map = leaflet.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    leaflet
      .tileLayer(TILE_URL, {
        maxZoom: 18,
        minZoom: 2,
        attribution: 'Map data (c) OpenStreetMap contributors',
        crossOrigin: true,
      })
      .addTo(map);

    map.setView([startingLocation.lat, startingLocation.lon], startingZoom);

    const marker = leaflet.marker([startingLocation.lat, startingLocation.lon]).addTo(map);

    const handleClick = (event: { latlng: { lat: number; lng: number } }) => {
      const next = { lat: event.latlng.lat, lon: event.latlng.lng };
      setDraftLocation(next);
      marker.setLatLng([next.lat, next.lon]);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
      map.remove();
    };
  }, [isOpen, initialLocation?.lat, initialLocation?.lon]);

  if (!isOpen) return null;

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog-card pin-dialog" role="dialog" aria-modal="true" aria-labelledby="pin-title">
        <p className="eyebrow">Pin mode</p>
        <h2 id="pin-title">Drop a pin to choose your city pool</h2>
        <p className="helper-text">
          Click anywhere on the map to set the pin. The 100 closest cities to that pin will be used for this run.
        </p>
        {mapReady ? (
          <div className="pin-map" ref={mapContainerRef} aria-label="Map picker" />
        ) : (
          <div className="notice error">
            Map failed to load. Please check your connection and try again.
          </div>
        )}
        <p className="helper-text pin-coordinates">
          Selected: {draftLocation.lat.toFixed(4)}, {draftLocation.lon.toFixed(4)}
        </p>
        <div className="dialog-actions">
          <button className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary solid" onClick={() => onConfirm(draftLocation)} disabled={!mapReady}>
            Start pin run
          </button>
        </div>
      </div>
    </div>
  );
}
